import * as React from 'react';
import { Tooltip, TooltipTrigger, TooltipContent } from './Tooltip';

// ─── Price-source indicator ───────────────────────────────────────────────────
// Lives on the unit-price field. Color tells where the price came from; the
// client's price list takes priority over the OC price (guaranteed in the
// backend). Tooltip uses the same simple single-line style as the header's
// "descargar resumen" button.
//   price_list                     → green  (from the client's price list — priority)
//   pdf                            → blue   (price taken from the OC/PDF)
//   catalog / price_list_missing   → yellow (Kame/DB catalog price → verify)
//   manual                         → gray   (price edited by hand)

interface PriceSourceMeta {
  color: string;
  label: string;
}

const PRICE_SOURCE_META: Record<string, PriceSourceMeta> = {
  price_list: { color: 'bg-green-300/60', label: 'Precio de la lista' },
  pdf: { color: 'bg-blue-300/60', label: 'Precio de la OC' },
  catalog: { color: 'bg-yellow-300/60', label: 'Precio de Kame' },
  price_list_missing: { color: 'bg-yellow-300/60', label: 'Precio de Kame' },
  manual: { color: 'bg-gray-400/60', label: 'Precio cambiado a mano' },
};

interface PriceSourceDotProps {
  /** The item's current price source (pdfPriceSource). Starts from the backend-stored
   *  value and is updated live when the user picks a source or edits the price by hand. */
  priceSource?: string;
  className?: string;
}

/**
 * Dot shown over the unit-price input indicating where the price came from.
 * Green = client's price list; blue = OC/PDF; yellow = Kame/catalog price; gray = manual.
 * Reflects the active source: the backend-stored one on load, and the user's
 * selection/manual edit after interaction.
 */
export function PriceSourceDot({ priceSource, className }: PriceSourceDotProps) {
  if (!priceSource) return null;
  const meta = PRICE_SOURCE_META[priceSource];
  if (!meta) return null;

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <span
          className={`absolute top-1 right-1 h-2 w-2 rounded-full ${meta.color} cursor-help ${className ?? ''}`}
          aria-label={meta.label}
        />
      </TooltipTrigger>
      <TooltipContent>{meta.label}</TooltipContent>
    </Tooltip>
  );
}
